import React, { useCallback, KeyboardEvent } from 'react';
import ReactTooltip from 'react-tooltip';
import { useTheme } from '@emotion/react';
import { FormattedMessage, useIntl } from 'react-intl';

import { SVGIcon, SVGIcons } from '@/components/atoms/Icon/SVGIcon';
import { Label } from '@/components/atoms/Label';
import { computeText } from '@/locales/utils';
import { Container, UnstyledTextarea, StyledButton, IconContainer } from './Textarea.style';

export type TextareaProps = {
    value?: string;
    placeholder?: string;
    error?: string;
    name?: string;
    style?: React.CSSProperties;
    onAdd?: () => void;
    labelId?: string;
    onChange?: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
    onBlur?: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
    onKeyDownCapture?: (e: React.KeyboardEvent<HTMLTextAreaElement>) => void;
    disabled?: boolean;
    required?: boolean;
    isTouched?: boolean;
    isDirty?: boolean;
    className?: string;
    readOnly?: boolean;
    rows?: number;
    cols?: number;
    maxLength?: number;
    minLength?: number;
    resize?: 'none' | 'both' | 'horizontal' | 'vertical';
};

export const Textarea = React.forwardRef<HTMLTextAreaElement, TextareaProps>(
    (
        {
            value,
            error,
            name = '',
            onChange,
            onBlur,
            onKeyDownCapture,
            placeholder = '',
            onAdd,
            style,
            labelId,
            disabled,
            required,
            isTouched,
            isDirty,
            className,
            readOnly,
            rows = 4,
            cols,
            maxLength,
            minLength,
            resize = 'vertical',
            ...rest
        },
        ref,
    ) => {
        const intl = useIntl();
        const theme = useTheme();

        const handleKeyPress = useCallback(
            (event: KeyboardEvent) => {
                if (event.key === 'Enter' && onAdd) {
                    onAdd();
                }
            },
            [onAdd],
        );

        return (
            <div style={{ opacity: disabled ? 0.75 : 1, ...style }}>
                {labelId && <Label labelId={labelId} inputId={labelId} required={required} />}
                <Container
                    error={error}
                    isTouched={isTouched && isDirty !== false}
                    data-testid="textarea-container"
                    className={className}
                    style={{ opacity: disabled || readOnly ? 0.7 : 1 }}
                >
                    <UnstyledTextarea
                        ref={ref}
                        value={value}
                        placeholder={computeText(intl, placeholder)}
                        onChange={onChange}
                        onKeyDownCapture={onKeyDownCapture}
                        onBlur={onBlur}
                        name={name}
                        id={labelId}
                        disabled={disabled}
                        onKeyPress={handleKeyPress}
                        readOnly={readOnly}
                        rows={rows}
                        cols={cols}
                        maxLength={maxLength}
                        minLength={minLength}
                        resize={resize}
                        {...rest}
                    />
                    {onAdd && (
                        <StyledButton onClick={onAdd} buttonType="primary">
                            <FormattedMessage id="button.add" />
                        </StyledButton>
                    )}
                    {error && (
                        <IconContainer>
                            <div data-tip data-for="error">
                                <SVGIcon icon={SVGIcons.DANGER} size={14} color={theme.color.bloodyMary} />
                            </div>
                            <ReactTooltip id="error" place="top" effect="solid">
                                <span>{error}</span>
                            </ReactTooltip>
                        </IconContainer>
                    )}
                    {!error && isTouched && isDirty !== false && (
                        <IconContainer>
                            <div data-tip data-for="touched">
                                <SVGIcon icon={SVGIcons.CHECK} size={14} color={theme.color.emerald} />
                            </div>
                        </IconContainer>
                    )}
                </Container>
            </div>
        );
    },
);
Textarea.displayName = 'Textarea';
